import { defineStore } from 'pinia'
import axios from '../axios.js'

export const useTeamMatchupStatsStore = defineStore('teamMatchupStats', {
  state: () => ({
    // Available leagues from stats database
    availableLeagues: [],
    
    // Current league stats
    currentLeagueId: null,
    currentLeagueName: null,
    teamStats: [],
    opponentStats: [],
    leagueAverages: {},
    
    // Mappings
    leagueMapping: null,
    teamMappings: {},
    
    // Selected teams for calculation
    selectedHomeTeam: null,
    selectedAwayTeam: null,
    
    // Calculation results
    homeProjections: {},
    awayProjections: {},
    
    // UI state
    loading: false,
    error: null,
    showCalculatorModal: false,
    showMappingModal: false,
    mappingType: null, // 'league' or 'team'
    mappingContext: null,
  }),

  getters: {
    // Get team names for dropdown
    teamNames: (state) => {
      return state.teamStats.map(t => t.team_name).filter(Boolean).sort()
    },
    
    // Get team stats by name
    getTeamStats: (state) => (teamName) => {
      return state.teamStats.find(t => t.team_name === teamName)
    },
    
    // Get opponent stats by name
    getOpponentStats: (state) => (teamName) => {
      return state.opponentStats.find(t => t.team_name === teamName)
    },
    
    // Check if we have data
    hasData: (state) => {
      return state.teamStats.length > 0 && state.opponentStats.length > 0
    },
    
    // Check if mapping is needed
    needsLeagueMapping: (state) => {
      return !state.leagueMapping && state.currentLeagueName
    },
    
    // Get all stat labels
    statLabels: () => ({
      ppg: 'Points',
      tpm: '3PM',
      rpg: 'Rebounds',
      apg: 'Assists',
      spg: 'Steals',
      bpg: 'Blocks',
      tov: 'Turnovers'
    }),
  },

  actions: {
    // Fetch all available leagues
    async fetchLeagues() {
      try {
        this.loading = true
        const response = await axios.get('api/team-matchup-stats/leagues')
        if (response.data.success) {
          this.availableLeagues = response.data.data
        }
      } catch (error) {
        console.error('Error fetching leagues:', error)
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    
    // Fetch league stats
    async fetchLeagueStats(leagueId, season = '2026') {
      try {
        this.loading = true
        this.error = null
        
        const response = await axios.get(
          `api/team-matchup-stats/leagues/${leagueId}/stats`,
          { params: { season } }
        )
        
        if (response.data.success) {
          this.currentLeagueId = leagueId
          this.teamStats = response.data.data.team_stats
          this.opponentStats = response.data.data.opponent_stats
          this.leagueAverages = response.data.data.league_averages
        }
      } catch (error) {
        console.error('Error fetching league stats:', error)
        this.error = error.message
      } finally {
        this.loading = false
      }
    },
    
    // Check and get league mapping from backend (no in-memory cache; always fresh from DB)
    async checkLeagueMapping(sourceLeagueName, sourceLeagueId = null) {
      try {
        const response = await axios.get(
          'api/team-matchup-stats/mappings/league',
          { params: { source_league_name: sourceLeagueName, source_league_id: sourceLeagueId } }
        )
        
        if (response.data.success) {
          this.leagueMapping = response.data.data
          this.availableLeagues = response.data.available_leagues || this.availableLeagues
          
          if (this.leagueMapping && this.currentLeagueId !== this.leagueMapping.stats_league_id) {
            await this.fetchLeagueStats(this.leagueMapping.stats_league_id)
          }
          
          return this.leagueMapping
        }
      } catch (error) {
        console.error('Error checking league mapping:', error)
      }
      this.leagueMapping = null
      return null
    },
    
    // Save league mapping (uses OC league ID for persistent mapping)
    async saveLeagueMapping(sourceLeagueName, statsLeagueId, sourceLeagueId = null) {
      try {
        const response = await axios.post(
          'api/team-matchup-stats/mappings/league',
          {
            source_league_name: sourceLeagueName,
            stats_league_id: statsLeagueId,
            source_league_id: sourceLeagueId
          }
        )
        
        if (response.data.success) {
          this.leagueMapping = response.data.data
          if (this.currentLeagueId !== statsLeagueId) {
            await this.fetchLeagueStats(statsLeagueId)
          }
          return true
        }
      } catch (error) {
        console.error('Error saving league mapping:', error)
        this.error = error.message
      }
      return false
    },
    
    // Check team mapping from backend (no in-memory cache; always fresh from DB)
    async checkTeamMapping(sourceTeamName, sourceTeamId = null, sourceLeagueId = null, statsLeagueId = null) {
      const cacheKey = sourceTeamId || sourceTeamName
      try {
        const response = await axios.get(
          'api/team-matchup-stats/mappings/team',
          { 
            params: { 
              source_team_name: sourceTeamName, 
              source_team_id: sourceTeamId,
              source_league_id: sourceLeagueId,
              stats_league_id: statsLeagueId || this.currentLeagueId
            } 
          }
        )
        
        if (response.data.success) {
          if (response.data.data) {
            this.teamMappings[cacheKey] = response.data.data.stats_team_name
            if (sourceTeamName !== cacheKey) {
              this.teamMappings[sourceTeamName] = response.data.data.stats_team_name
            }
          } else {
            this.teamMappings[cacheKey] = null
          }
          return {
            mapping: response.data.data,
            availableTeams: response.data.available_teams
          }
        }
      } catch (error) {
        console.error('Error checking team mapping:', error)
      }
      return null
    },
    
    // Save team mapping (uses OC IDs for persistent mapping)
    async saveTeamMapping(sourceTeamName, statsTeamName, sourceTeamId = null, sourceLeagueId = null, statsLeagueId = null) {
      try {
        const response = await axios.post(
          'api/team-matchup-stats/mappings/team',
          {
            source_team_name: sourceTeamName,
            stats_team_name: statsTeamName,
            source_team_id: sourceTeamId,
            source_league_id: sourceLeagueId,
            stats_league_id: statsLeagueId || this.currentLeagueId
          }
        )
        
        if (response.data.success) {
          // Cache by both team name and ID
          this.teamMappings[sourceTeamName] = statsTeamName
          if (sourceTeamId) {
            this.teamMappings[sourceTeamId] = statsTeamName
          }
          return true
        }
      } catch (error) {
        console.error('Error saving team mapping:', error)
        this.error = error.message
      }
      return false
    },
    
    // Calculate matchup locally (for quick calculations)
    calculateMatchupLocally(homeTeamName, awayTeamName) {
      const homeTeam = this.getTeamStats(homeTeamName)
      const awayDefense = this.getOpponentStats(awayTeamName)
      
      console.log('🔢 calculateMatchupLocally:', {
        homeTeamName, 
        awayTeamName,
        homeTeamFound: !!homeTeam,
        awayDefenseFound: !!awayDefense,
        availableTeams: this.teamStats?.map(t => t.team_name).slice(0, 5),
        availableOpponents: this.opponentStats?.map(t => t.team_name).slice(0, 5)
      })
      
      if (!homeTeam || !awayDefense) {
        console.warn('⚠️ Missing data:', { homeTeam, awayDefense })
        return null
      }
      
      const stats = ['ppg', 'tpm', 'rpg', 'apg', 'spg', 'bpg', 'tov']
      const projections = {}
      
      stats.forEach(stat => {
        const homeStat = homeTeam[stat] || 0
        const oppStat = awayDefense[stat] || 0
        const leagueAvg = this.leagueAverages[stat] || 0
        
        const muVsLeagueAvg = oppStat - leagueAvg
        const muValue = leagueAvg > 0 ? muVsLeagueAvg / leagueAvg : 0
        const finalValue = (homeStat * muValue) + homeStat
        
        projections[stat] = {
          homeStat: homeStat,
          oppStat: oppStat,
          leagueAvg: leagueAvg,
          muVsAvg: muVsLeagueAvg,
          muValue: muValue,
          muPercent: muValue * 100,
          projected: finalValue
        }
      })
      
      return projections
    },
    
    // Calculate both directions
    calculateBothMatchups(teamAName, teamBName) {
      // Team A as home
      const teamAProjections = this.calculateMatchupLocally(teamAName, teamBName)
      
      // Team B as home
      const teamBProjections = this.calculateMatchupLocally(teamBName, teamAName)
      
      return {
        teamA: {
          name: teamAName,
          projections: teamAProjections
        },
        teamB: {
          name: teamBName,
          projections: teamBProjections
        }
      }
    },
    
    // Calculate matchup via API
    async calculateMatchupApi(leagueId, homeTeam, awayTeam, season = '2026') {
      try {
        this.loading = true
        const response = await axios.post(
          'api/team-matchup-stats/calculate-both-matchups',
          {
            league_id: leagueId,
            team_a: homeTeam,
            team_b: awayTeam,
            season
          }
        )
        
        if (response.data.success) {
          this.homeProjections = response.data.data.team_a.projections_as_home
          this.awayProjections = response.data.data.team_b.projections_as_home
          return response.data.data
        }
      } catch (error) {
        console.error('Error calculating matchup:', error)
        this.error = error.message
      } finally {
        this.loading = false
      }
      return null
    },
    
    // Set selected teams
    setSelectedTeams(homeTeam, awayTeam) {
      this.selectedHomeTeam = homeTeam
      this.selectedAwayTeam = awayTeam
    },
    
    // Open calculator modal
    openCalculatorModal() {
      this.showCalculatorModal = true
    },
    
    // Close calculator modal
    closeCalculatorModal() {
      this.showCalculatorModal = false
    },
    
    // Open mapping modal
    openMappingModal(type, context) {
      this.mappingType = type
      this.mappingContext = context
      this.showMappingModal = true
    },
    
    // Close mapping modal
    closeMappingModal() {
      this.showMappingModal = false
      this.mappingType = null
      this.mappingContext = null
    },
    
    // Reset state
    reset() {
      this.currentLeagueId = null
      this.currentLeagueName = null
      this.teamStats = []
      this.opponentStats = []
      this.leagueAverages = {}
      this.leagueMapping = null
      this.teamMappings = {}
      this.selectedHomeTeam = null
      this.selectedAwayTeam = null
      this.homeProjections = {}
      this.awayProjections = {}
      this.error = null
    },
    
    // Clear mapping state so next load fetches fresh from backend
    resetCache() {
      this.leagueMapping = null
      this.teamMappings = {}
    }
  }
})
